import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Actions
import {
    setSelectedTableColumnName,
    setSelectedTableCell,
    addSelectedTableRow,
    removeSelectedTableRow
} from 'services/selectedTableCells/actions';

// Selectors
import {
    selectSelectableTableColumns,
    selectOrderedData
} from 'services/timesheetTimesheetEntriesForm/selectors';
import {
    selectSelectedTableCellsColumnName,
    selectSelectedTableCellRows,
    selectLastSelectedTableRow
} from 'services/selectedTableCells/selectors';
import { makeSelectQueryStringBooleanValue } from 'workspaces/react-router-selectors';

interface Props {
    selectableTableColumns: {
        name: string,
        isPieceRateField?: boolean
    }[];
    orderedData: {
        workTypeCompositeKey: string,
        uuid: number
    }[];
    rows: string[];
    lastSelectedTableCell?: string;
    columnName?: string;
    row?: string;
    setSelectedTableCell: Function;
    addSelectedTableRow: Function;
    removeSelectedTableRow: Function;
    isModalOpen: boolean;
}

export class TimesheetKeyboardEventsListener extends React.PureComponent<Props> {
    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyPress);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyPress);
    }

    handleKeyPress = (event: any) => {
        const {
            columnName,
            row,
            selectableTableColumns,
            orderedData,
            isModalOpen,
            ...props
        } = this.props;

        if (!isModalOpen && row) {
            // ArrowLeft/ArrowRight/Tab
            if ([37, 39, 9].includes(event.keyCode)) {
                event.preventDefault();
                const direction = event.keyCode === 37 ? -1 : 1;

                const { workTypeCompositeKey } =
                    orderedData.find(value => value.uuid === row) || {};

                const [, isPieceRateString] = workTypeCompositeKey
                    ? workTypeCompositeKey.split('.')
                    : [];
                const isPieceRate = isPieceRateString === 'true';

                const filteredSelectableTableColumns = selectableTableColumns.filter(
                    value => (!isPieceRate ? !value.isPieceRateField : true)
                );

                const cellNameIndex = filteredSelectableTableColumns.findIndex(
                    value => value.name === columnName
                );
                const nextCellNameIndex = cellNameIndex + direction;
                const nextCellName = (
                    filteredSelectableTableColumns[nextCellNameIndex] || {}
                ).name;

                if (nextCellName) {
                    if (event.shiftKey) {
                        props.setSelectedTableColumnName(nextCellName);
                    } else {
                        props.setSelectedTableCell(row, nextCellName);
                    }
                }
            }
            // ArrowUp/ArrowDown
            if ([38, 40].includes(event.keyCode)) {
                event.preventDefault();
                const direction = event.keyCode === 38 ? -1 : 1;

                const { rows } = this.props;

                const { isPieceRateField } =
                    selectableTableColumns.find(
                        value => value.name === columnName
                    ) || {};

                const filteredOrderedData = orderedData.filter(value => {
                    // Prevent approved entries being navigated to
                    if (value.lastReviewIsApproved) {
                        return false;
                    }
                    if (isPieceRateField) {
                        const [, isPieceRateString] = value.workTypeCompositeKey
                            ? value.workTypeCompositeKey.split('.')
                            : [];
                        const isPieceRate = isPieceRateString === 'true';
                        return isPieceRate;
                    }
                    return true;
                });

                const rowIndex = filteredOrderedData.findIndex(
                    value => value.uuid === row
                );
                const nextRowIndex = rowIndex + direction;
                const nextRowId = (filteredOrderedData[nextRowIndex] || {})
                    .uuid;

                if (nextRowId) {
                    if (event.shiftKey) {
                        const nextRowIndexIsSelected = rows.includes(nextRowId);

                        if (nextRowIndexIsSelected) {
                            props.removeSelectedTableRow(row);
                        } else {
                            props.addSelectedTableRow(nextRowId);
                        }
                    } else {
                        props.setSelectedTableCell(nextRowId, columnName);
                    }
                }
            }
        }
    };

    render() {
        return null;
    }
}

const mapStateToProps = createStructuredSelector({
    selectableTableColumns: selectSelectableTableColumns,
    orderedData: selectOrderedData,
    rows: selectSelectedTableCellRows,
    columnName: selectSelectedTableCellsColumnName,
    row: selectLastSelectedTableRow,
    isModalOpen: makeSelectQueryStringBooleanValue('Modal')
});

export default connect(
    mapStateToProps,
    {
        setSelectedTableColumnName,
        setSelectedTableCell,
        addSelectedTableRow,
        removeSelectedTableRow
    }
)(TimesheetKeyboardEventsListener);
